import { Pressable, StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import { tokens } from '../../theme/tokens';
import { AppText } from './AppText';

export type AppSegmentedControlOption<T extends string> = {
  id: T;
  label: string;
};

type AppSegmentedControlProps<T extends string> = {
  options: AppSegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  style?: StyleProp<ViewStyle>;
};

export function AppSegmentedControl<T extends string>({
  options,
  value,
  onChange,
  style,
}: AppSegmentedControlProps<T>) {
  return (
    <View style={[styles.container, style]}>
      {options.map((option) => {
        const active = option.id === value;
        return (
          <Pressable
            key={option.id}
            accessibilityRole="button"
            accessibilityState={{ selected: active }}
            onPress={() => onChange(option.id)}
            style={[styles.option, active ? styles.optionActive : null]}
          >
            <AppText
              variant="caption"
              weight="bold"
              tone={active ? 'default' : 'primary'}
              style={active ? styles.optionTextActive : null}
            >
              {option.label}
            </AppText>
          </Pressable>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    minHeight: tokens.component.segmentedControl.height,
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: tokens.component.segmentedControl.radius,
    padding: tokens.component.segmentedControl.padding,
    backgroundColor: tokens.colors.surfaceMuted,
  },
  option: {
    minHeight: tokens.component.segmentedControl.height - tokens.component.segmentedControl.padding * 2,
    minWidth: 76,
    borderRadius: tokens.component.segmentedControl.radius,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: tokens.spacing.md,
  },
  optionActive: {
    backgroundColor: tokens.colors.primary,
  },
  optionTextActive: {
    color: tokens.colors.white,
  },
});
